import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { accountsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES, formatRange, formatMonth } from '../lib/period';
import { 
  Building2, Users, FileText, CreditCard, TrendingUp, TrendingDown,
  AlertTriangle, IndianRupee, Receipt, Calculator, X
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import SyncStatusPill from '../components/SyncStatusPill';
import InlineIntegrityChip from '../components/InlineIntegrityChip';
import UnpaidOrdersCard from '../components/UnpaidOrdersCard';

// Label for the window the backend actually used.
const formatWindow = (dash) => {
  if (!dash?.period_start) return '-';
  if (dash.period_source === 'MONTH') return formatMonth(dash.period_start.slice(0, 7));
  return formatRange(dash.period_start, dash.period_end);
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const KPICard = ({ title, value, icon: Icon, trend, trendValue, variant = 'default', onClick, isCount = false, subtitle }) => {
  const variants = {
    default: 'border-border/50',
    success: 'border-emerald-500/30 bg-emerald-500/5',
    warning: 'border-amber-500/30 bg-amber-500/5',
    danger: 'border-rose-500/30 bg-rose-500/5',
    info: 'border-blue-500/30 bg-blue-500/5',
  };

  const iconVariants = {
    default: 'text-muted-foreground',
    success: 'text-emerald-400',
    warning: 'text-amber-400',
    danger: 'text-rose-400',
    info: 'text-blue-400',
  };

  return (
    <Card 
      className={`bg-card/50 backdrop-blur-sm ${variants[variant]} card-hover ${onClick ? 'cursor-pointer' : ''}`} 
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-mono font-semibold tracking-tight tabular-nums">
              {isCount ? value : (typeof value === 'number' ? formatCurrency(value) : value)}
            </p>
            {subtitle && (
              <p className="text-[11px] text-muted-foreground" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}-period`}>{subtitle}</p>
            )}
            {trend && (
              <div className={`flex items-center text-xs ${trend === 'up' ? 'text-emerald-400' : 'text-rose-400'}`}>
                {trend === 'up' ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {trendValue}
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-background/50 ${iconVariants[variant]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CHART_TOOLTIP_STYLE = { backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px' };
const CHART_LABEL_STYLE = { color: '#FAFAFA' };

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  // legacy `syncing` state removed alongside the decommissioned Sync Data button
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);
  const [showSpendBreakdown, setShowSpendBreakdown] = useState(false);
  const [spendData, setSpendData] = useState(null);
  const [loadingSpend, setLoadingSpend] = useState(false);
  const periodState = usePeriod(PERIOD_MODES.RANGE);
  const windowParams = periodState.apiParams;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashboardRes, analyticsRes] = await Promise.all([
        accountsAPI.getDashboard(windowParams),
        accountsAPI.getSpendAnalytics(windowParams)
      ]);
      setDashboard(dashboardRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
    setLoading(false);
  }, [windowParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Legacy `handleSync` was wired to POST /api/sync/all — permanently
  // decommissioned (returns 410 Gone). Vendor/bill state is read-through-
  // proxied from Expense Capture; sales data is auto-synced from Sales
  // Capture via pulse_scheduler + startup catch-up. Operator-controlled
  // backfill exists at POST /api/accounts/sales-source/backfill via
  // accountsAPI.salesSourceBackfill — never label it "Sync Vendors/Bills".
  // The button + handler have been removed from this page.

  const handleSpendClick = async () => {
    setShowSpendBreakdown(true);
    setLoadingSpend(true);
    try {
      const res = await accountsAPI.getSpendBreakdown(windowParams);
      setSpendData(res.data);
    } catch (err) {
      toast.error('Failed to load spend breakdown');
    }
    setLoadingSpend(false);
  };

  const periodLabel = formatWindow(dashboard);

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Financial overview for {user?.org_name || 'your organization'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <InlineIntegrityChip surface="dashboard" />
          <SyncStatusPill />
          <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE} resolved={dashboard} />
          {/* Source badges — replace the legacy "Sync Data" button. */}
          <div className="flex items-center gap-2 text-[11px]" data-testid="dashboard-source-badges">
            <span className="px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/5 text-emerald-300"
                  title="Sales Capture data is auto-synced via the scheduler. Use Operations → Backfill for manual recovery.">
              Sales: Sales Capture · Live sync
            </span>
            <span className="px-2 py-1 rounded-md border border-blue-500/30 bg-blue-500/5 text-blue-300"
                  title="Vendor/bill data is read live from Expense Capture. No local mirror.">
              Bills: Expense Capture · Live read
            </span>
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={`skeleton-${i}`} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : dashboard && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Total Vendors"
              value={dashboard.total_vendors}
              subtitle={`Registry · ${dashboard.vendors_in_window ?? 0} with bills in ${periodLabel}`}
              icon={Users}
              variant="info"
              isCount={true}
              onClick={() => navigate('/vendors')}
            />
            <KPICard
              title="Total Bills"
              value={dashboard.total_bills}
              subtitle={periodLabel}
              icon={FileText}
              isCount={true}
              onClick={() => navigate('/bills')}
            />
            <KPICard
              title="Total Outstanding"
              value={dashboard.total_outstanding}
              subtitle={`On bills dated ${periodLabel}`}
              icon={CreditCard}
              variant={dashboard.total_outstanding > 100000 ? 'warning' : 'default'}
              onClick={() => navigate('/vendors')}
            />
            <KPICard
              title={dashboard.period_source === 'MONTH' ? 'Monthly Spend' : 'Spend'}
              value={dashboard.current_month_spend}
              subtitle={periodLabel}
              icon={IndianRupee}
              variant="success"
              onClick={handleSpendClick}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Payments Made"
              value={dashboard.current_month_payments || 0}
              subtitle={`On bills dated ${periodLabel}`}
              icon={Receipt}
              variant="success"
            />
            <KPICard
              title="Est. Tax Liability"
              value={dashboard.estimated_tax_liability || 0}
              subtitle={periodLabel}
              icon={Calculator}
              variant={dashboard.estimated_tax_liability > 0 ? 'warning' : 'success'}
              onClick={() => setShowTaxBreakdown(true)}
            />
            <KPICard
              title="Total Sales"
              value={dashboard.total_monthly_sales || 0}
              subtitle={periodLabel}
              icon={TrendingUp}
              variant="success"
            />
            <KPICard
              title="Overdue Bills"
              value={dashboard.overdue_bills}
              subtitle={`>30 days old · dated ${periodLabel}`}
              icon={AlertTriangle}
              variant={dashboard.overdue_bills > 0 ? 'danger' : 'default'}
              isCount={true}
            />
          </div>

          {/* Tax Breakdown Popup */}
          {showTaxBreakdown && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowTaxBreakdown(false)}>
              <Card className="w-full max-w-md mx-4 bg-card border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-semibold">Tax Liability Breakdown</CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setShowTaxBreakdown(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-xs text-muted-foreground">Period: {periodLabel}</p>
                  
                  {/* GST Section */}
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <div>
                      <p className="font-medium">GST</p>
                      <p className="text-xs text-muted-foreground">Sales GST from POS</p>
                    </div>
                    <p className="text-lg font-mono font-semibold text-amber-400">
                      {formatCurrency(dashboard.sales_gst || 0)}
                    </p>
                  </div>
                  
                  {/* VAT Section */}
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <div>
                      <p className="font-medium">VAT</p>
                      <p className="text-xs text-muted-foreground">Output VAT - Input VAT</p>
                    </div>
                    <p className={`text-lg font-mono font-semibold ${(dashboard.net_vat_liability || 0) >= 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {formatCurrency(dashboard.net_vat_liability || 0)}
                    </p>
                  </div>
                  
                  {/* TDS Section */}
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <div>
                      <p className="font-medium">TDS</p>
                      <p className="text-xs text-muted-foreground">Tax Deducted at Source</p>
                    </div>
                    <p className="text-lg font-mono font-semibold text-muted-foreground">
                      {formatCurrency(dashboard.tds_liability || 0)}
                    </p>
                  </div>
                  
                  {/* Total */}
                  <div className="flex items-center justify-between pt-2">
                    <p className="font-semibold text-lg">Total Estimated</p>
                    <p className="text-xl font-mono font-bold text-primary">
                      {formatCurrency(dashboard.estimated_tax_liability || 0)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Spend Breakdown Modal */}
          {showSpendBreakdown && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowSpendBreakdown(false)}>
              <Card className="w-full max-w-2xl mx-4 bg-card border-border shadow-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="spend-breakdown-modal">
                <CardHeader className="flex flex-row items-center justify-between pb-2 shrink-0">
                  <div>
                    <CardTitle className="text-lg font-semibold">Spend Breakdown</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1" data-testid="spend-breakdown-period">{spendData ? formatWindow(spendData) : periodLabel}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setShowSpendBreakdown(false)} data-testid="close-spend-modal">
                    <X className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="overflow-y-auto flex-1">
                  {loadingSpend && <div className="text-center text-muted-foreground py-8">Loading...</div>}
                  {spendData && !loadingSpend && (
                    <div className="space-y-5">
                      {/* Summary row */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-muted/30 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Total Spend</p>
                          <p className="text-xl font-mono font-bold text-primary mt-1">{formatCurrency(spendData.total_spend)}</p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Bills</p>
                          <p className="text-xl font-mono font-bold mt-1">{spendData.bill_count}</p>
                        </div>
                        <div className="bg-muted/30 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">Vendors</p>
                          <p className="text-xl font-mono font-bold mt-1">{spendData.vendor_count}</p>
                        </div>
                      </div>

                      {/* By Vendor */}
                      {spendData.by_vendor?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">By Vendor</h4>
                          <div className="space-y-1">
                            {spendData.by_vendor.map((v, i) => {
                              const pct = spendData.total_spend > 0 ? (v.total / spendData.total_spend * 100) : 0;
                              return (
                                <div key={i} className="flex items-center gap-3 py-2 border-b border-border/30 last:border-0" data-testid={`vendor-row-${i}`}>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm font-medium truncate">{v.vendor_name}</span>
                                      <span className="text-sm font-mono font-semibold shrink-0 ml-2">{formatCurrency(v.total)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                                      </div>
                                      <span className="text-[10px] text-muted-foreground shrink-0 w-14 text-right">{pct.toFixed(1)}% · {v.count} bill{v.count !== 1 ? 's' : ''}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* All Bills */}
                      {spendData.bills?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">All Bills ({spendData.bill_count})</h4>
                          <div className="border border-border/50 rounded-lg overflow-hidden">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-muted/30">
                                  <th className="text-left p-2 font-medium text-muted-foreground">Date</th>
                                  <th className="text-left p-2 font-medium text-muted-foreground">Vendor</th>
                                  <th className="text-left p-2 font-medium text-muted-foreground">Bill #</th>
                                  <th className="text-right p-2 font-medium text-muted-foreground">Amount</th>
                                  <th className="text-center p-2 font-medium text-muted-foreground">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {spendData.bills.map((b, i) => (
                                  <tr key={i} className="border-t border-border/20 hover:bg-muted/10" data-testid={`bill-row-${i}`}>
                                    <td className="p-2 text-muted-foreground">{b.bill_date?.slice(5)}</td>
                                    <td className="p-2 truncate max-w-[150px]">{b.vendor_name}</td>
                                    <td className="p-2 text-muted-foreground">{b.bill_number}</td>
                                    <td className="p-2 text-right font-mono">{formatCurrency(b.total_amount)}</td>
                                    <td className="p-2 text-center">
                                      <Badge variant="outline" className={`text-[9px] ${
                                        b.payment_status === 'PAID' || b.payment_status === 'Paid' ? 'text-emerald-400 border-emerald-500/30' :
                                        'text-amber-400 border-amber-500/30'
                                      }`}>{b.payment_status}</Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Bills by Status */}
          {dashboard.bills_by_status && Object.keys(dashboard.bills_by_status).length > 0 && (
            <Card className="bg-card/50 backdrop-blur-sm border-border/50">
              <CardHeader>
                <CardTitle className="text-lg font-manrope">Bills by Status</CardTitle>
                <p className="text-xs text-muted-foreground">{periodLabel}</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(dashboard.bills_by_status).map(([status, data]) => (
                    <div key={status} className="text-center p-4 rounded-lg bg-muted/30">
                      <Badge 
                        variant="outline" 
                        className={
                          status === 'Paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                          status === 'Partial' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                          status === 'Unpaid' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                          ''
                        }
                      >
                        {status}
                      </Badge>
                      <p className="text-2xl font-mono font-bold mt-2">{data.count}</p>
                      <p className="text-sm text-muted-foreground">{formatCurrency(data.total)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {dashboard.overdue_bills > 0 && (
            <Card className="bg-rose-500/5 border-rose-500/30" data-testid="overdue-alert">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-rose-400" />
                  <div>
                    <p className="font-medium text-rose-400">{dashboard.overdue_bills} Overdue Bills</p>
                    <p className="text-sm text-muted-foreground">Bills dated {periodLabel}, older than 30 days, still unpaid</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Unpaid Orders Operational Breakdown — Phase 5 */}
      <UnpaidOrdersCard />

      {/* Charts */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Spend by Month */}
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-manrope">Monthly Spend Trend</CardTitle>
              <p className="text-xs text-muted-foreground">{periodLabel}</p>
            </CardHeader>
            <CardContent>
              {analytics.by_month?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.by_month}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="_id" stroke="#71717A" fontSize={12} />
                    <YAxis stroke="#71717A" fontSize={12} tickFormatter={(v) => `₹${(v/1000)}K`} />
                    <Tooltip
                      contentStyle={CHART_TOOLTIP_STYLE}
                      labelStyle={CHART_LABEL_STYLE}
                      formatter={(value) => [formatCurrency(value), 'Amount']}
                    />
                    <Bar dataKey="total_amount" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available — vendor/bill data is fetched live from Expense Capture.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Vendors */}
          <Card className="bg-card/50 backdrop-blur-sm border-border/50">
            <CardHeader>
              <CardTitle className="text-lg font-manrope">Top Vendors by Spend</CardTitle>
              <p className="text-xs text-muted-foreground">{periodLabel}</p>
            </CardHeader>
            <CardContent>
              {analytics.by_vendor?.length > 0 ? (
                <div className="space-y-4">
                  {analytics.by_vendor.slice(0, 5).map((vendor, i) => (
                    <div key={vendor.vendor_id} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${COLORS[i]}20`, color: COLORS[i] }}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{vendor.vendor_name}</p>
                        <p className="text-xs text-muted-foreground">{vendor.bill_count} bills</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-semibold">{formatCurrency(vendor.total_amount)}</p>
                        {vendor.outstanding > 0 && (
                          <p className="text-xs text-amber-400">{formatCurrency(vendor.outstanding)} due</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trial Spend */}
      {analytics?.trial_spend > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="font-medium text-amber-400">Trial Spend</p>
                  <p className="text-sm text-muted-foreground">Expenses marked as trial/testing · {periodLabel}</p>
                </div>
              </div>
              <p className="font-mono text-xl font-semibold text-amber-400">
                {formatCurrency(analytics.trial_spend)}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
